// My Patients — clinician's own patient list + order history. Card-list
// pattern (matches MySummaries.js/MyPayments.js, not a dense table — this
// app's existing clinician-adjacent pages favor spacious cards). Scoped
// strictly to this clinician's own account server-side; this page trusts
// that scoping rather than re-implementing it.
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box, Typography, TextField, CircularProgress, Card, Chip, Button,
  InputAdornment, Avatar, Alert,
} from "@mui/material";
import { ArrowBack, Search, ExpandMore, ExpandLess, Download, Science, Medication, Coronavirus, Visibility } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import axios from "axios";
import UserContext from "./UserContext";
import { AddressFields, useAddressConfirm } from "./addressShared";
import { formatEST } from "../dateUtils";
import ClinicianPageHeader from "./ClinicianPageHeader";
import DynamicRequisitionForm from "./DynamicRequisitionForm";
import { extractErrorDetail } from "./validators";
// 2026-10-13 — extracted to src/testDisplayNames.js so PatientConsent.js
// (the patient-facing signing page) can reuse the EXACT same map instead
// of a second, hand-typed copy drifting out of sync (see that file's own
// comment for why a raw placeholder code there is an even worse bug).
import { TEST_ID_DISPLAY_NAMES } from "../testDisplayNames";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const BRAND = { navy: "#00357a", navyDark: "#001f4d", gold: "#c9a96e" };

// Supporting Documents — read-only here (no role-based hiding: a staff/
// admin-added doc is listed exactly like a clinician-added one). Mirrors
// the backend's SUPPORTING_DOC_TYPES allowlist.
const SUPPORTING_DOC_TYPE_LABELS = {
  patient_id: "Patient ID",
  insurance_card: "Insurance Card",
  drivers_license: "Driver's License",
  abn: "ABN",
  pa: "PA — Prior Auth",
  other: "Other",
};

// 2026-10-12 fix — order-level status Chip showed the raw backend value
// ("finalized") with no plain-language meaning to a clinician. Per-
// requisition values (clinician_signed/patient_signed) are included for
// completeness/future-proofing even though this app's order-level
// `status` field is only ever "finalized" today.
// 2026-10-21 fix (chunk-2 line-by-line trace finding) — both non-
// "finalized" labels were backwards: "clinician_signed" names the state
// where the CLINICIAN HAS already signed (so the order is awaiting the
// PATIENT), and vice versa for "patient_signed". order.status is always
// literally "finalized" today (confirmed by reading submit_clinician_
// order's own order_doc construction) — this dead code never currently
// renders — but fixed now so it reads correctly the moment the lab side
// ever writes either of these two values at the order level.
const ORDER_STATUS_DISPLAY = {
  clinician_signed: "Awaiting Patient Signature",
  patient_signed: "Awaiting Clinician Signature",
  finalized: "Order Submitted",
};

// 2026-10-10 — the clinical/billing field set now persisted directly on
// clinician_patient_records (_PATIENT_RECORD_CLINICAL_FIELD_IDS on the
// backend). Kept here as the SAME list, hand-synced across the app/
// language boundary exactly like orderTestDerivation.js's own
// PATIENT_RECORD_CLINICAL_FIELD_IDS does — this is the plain JS array this
// file needs to build the form's initial state / PUT payload from, without
// re-deriving it from the schema (the schema's SECTION/field list is used
// only for labels/options/types, never to decide WHICH keys to send).
const CLINICAL_FIELD_IDS = [
  "sex", "height_ft", "height_in", "weight_lbs", "race", "middle_initial",
  "bill_to", "policy_number", "group_number", "insurance_provider", "subscriber_name", "relationship",
];

function emptyClinicalForm() {
  return Object.fromEntries(CLINICAL_FIELD_IDS.map((fid) => [fid, fid === "race" ? [] : ""]));
}

// Reads the patient record's own clinical fields into the shape
// DynamicRequisitionForm expects (never undefined/null — a number/text
// field renders as "", a checkbox_group as []).
function clinicalFormFromPatient(p) {
  const out = emptyClinicalForm();
  for (const fid of CLINICAL_FIELD_IDS) {
    const v = p?.[fid];
    if (v === undefined || v === null) continue;
    out[fid] = v;
  }
  return out;
}

// One schema (any family's — SHARED_SECTIONS is byte-identical across all
// 3, confirmed in OrderTest.js) supplies the real labels/options/types for
// these fields, fetched through this app's own clinician-authenticated
// proxy — same source OrderTest.js's Step 3/4 uses, so My Patients' Edit
// form never invents a second, possibly-drifted copy of these labels.
const _SCHEMA_KEY_FOR_FIELD_LABELS = "proove_pgx_v1";

function initialsOf(patient) {
  return `${(patient.first_name || "")[0] || ""}${(patient.last_name || "")[0] || ""}`.toUpperCase() || "P";
}

// Card-list pattern, same shell as MySummaries.js (border/radius/shadow),
// with an initials avatar for visual hierarchy a plain row doesn't have.
function PatientCard({ patient, onOpen, isDark }) {
  return (
    <Card onClick={() => onOpen(patient.id)} sx={{
      p: 2.25, mb: 1.5, borderRadius: 3, cursor: "pointer",
      border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,53,122,0.1)",
      bgcolor: isDark ? "#111827" : "#fff",
      boxShadow: isDark ? "0 4px 20px rgba(0,0,0,0.3)" : "0 4px 20px rgba(0,53,122,0.06)",
      transition: "all 0.18s ease",
      "&:hover": {
        boxShadow: isDark ? "0 10px 28px rgba(0,0,0,0.4)" : "0 10px 28px rgba(0,53,122,0.14)",
        borderColor: isDark ? "rgba(255,255,255,0.3)" : "rgba(0,53,122,0.3)",
        transform: "translateY(-2px)",
      },
    }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.75, minWidth: 0 }}>
          <Avatar sx={{
            width: 42, height: 42, fontSize: 14.5, fontWeight: 800,
            bgcolor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,53,122,0.08)", color: isDark ? BRAND.gold : BRAND.navy,
          }}>
            {initialsOf(patient)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 15.5, color: isDark ? "#e5e7eb" : "#1e293b" }}>
              {patient.first_name} {patient.last_name}
            </Typography>
            <Typography sx={{ fontSize: 13, color: isDark ? "#94a3b8" : "#64748b" }}>DOB {patient.dob}</Typography>
          </Box>
        </Box>
        <Typography sx={{ fontSize: 12.5, color: isDark ? "#64748b" : "#94a3b8", flexShrink: 0, textAlign: "right" }}>
          Updated<br />{patient.updated_at ? formatEST(patient.updated_at) : "—"}
        </Typography>
      </Box>
    </Card>
  );
}

// Order a Test, Part B — an order can now carry more than one rendered
// requisition (one per distinct test family). Each gets its OWN download
// button, disambiguated by schema_key against the backend's
// requisition-url?schema_key= param — never just one blanket button that
// can only ever reach whichever requisition happened to be first.
// 2026-10-13 fix — this page used to be download-only for a past order's
// requisition PDF, same gap OrderTest.js's Review & Sign step had
// pre-submission. Ports labariumaireport/frontend's LabOrdersView.js
// InlinePreviewFrame/RequisitionHeaderRow pattern (same frame styling/
// fade-in behavior, same signed-URL staleness guard/expiry timers) rather
// than inventing a new one — this is the proven pattern that already had
// its own stale-cached-URL/layout-shift/missing-loading-state bugs found
// and fixed once this session; no reason to reintroduce them here.
// Signed URL lifetime server-side is 30 minutes (see
// get_clinician_order_requisition_url's generate_signed_url call in
// main.py) — SAFE_URL_LIFETIME_MS/REAL_URL_LIFETIME_MS below are the same
// two constants LabOrdersView.js uses for the same reason.
const SAFE_URL_LIFETIME_MS = 25 * 60 * 1000;
const REAL_URL_LIFETIME_MS = 30 * 60 * 1000;

function InlinePreviewFrame({ url, onError }) {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(false);
    const t = setTimeout(() => setLoaded(true), 4000);
    return () => clearTimeout(t);
  }, [url]);

  const frameSx = {
    mt: 1, borderRadius: "12px", overflow: "hidden",
    height: "min(600px, 60vh)",
    boxShadow: "0 8px 40px rgba(0,0,0,0.12)", border: "1px solid rgba(0,53,122,0.08)",
    bgcolor: "#f4f7ff",
  };
  const fadeStyle = { opacity: loaded ? 1 : 0, transition: "opacity 0.25s ease" };

  return (
    <Box sx={{ ...frameSx, display: "flex" }}>
      <iframe
        key={url} src={url} title="Requisition Preview"
        style={{ width: "100%", height: "100%", border: "none", ...fadeStyle }}
        onLoad={() => setLoaded(true)} onError={onError}
      />
    </Box>
  );
}

// Download + inline-Preview, paired in one header row, and — critically —
// the whole thing (header row AND the conditional preview panel) lives
// inside ONE block-level Box per requisition, never inside a `display:
// flex` row itself. The panel's iframe is `width: 100%`; nested directly
// inside a flex row, a flex item shrinks-to-fit its content by default,
// collapsing the whole 600px-tall preview down to a ~300px sliver next to
// the buttons instead of a full-width panel below them (caught in review
// before this shipped — matches LabOrdersView's own RequisitionHeaderRow
// shape: buttons in the header row, panel as a sibling below it, same
// outer Box). The caller list (OrderRow below) is itself a `flexDirection:
// "column"` flex container, whose children stretch to full width by
// default — so this component's own outer Box needs no explicit width
// rule to fill it.
function RequisitionRow({ orderId, schemaKey, token, downloadLabel, badge, patientSigned }) {
  const [downloading, setDownloading] = useState(false);
  const [open, setOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewFetchedAt, setPreviewFetchedAt] = useState(null);
  const [loadingUrl, setLoadingUrl] = useState(false);
  const [error, setError] = useState("");
  const [expired, setExpired] = useState(false);
  const expiryTimerRef = useRef(null);

  useEffect(() => () => { if (expiryTimerRef.current) clearTimeout(expiryTimerRef.current); }, []);

  const download = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API_BASE}/clinician/orders/${orderId}/requisition-url`, {
        headers: { Authorization: `Bearer ${token}` },
        params: schemaKey ? { schema_key: schemaKey } : undefined,
      });
      window.open(res.data.url, "_blank", "noopener,noreferrer");
    } catch {
      // best-effort — no toast system in this app yet; button simply
      // stays clickable to retry rather than silently failing forever.
    } finally {
      setDownloading(false);
    }
  };

  // Same GET /clinician/orders/{order_id}/requisition-url endpoint
  // download() above uses, just with ?disposition=inline so the browser
  // renders the PDF instead of downloading it (see
  // get_clinician_order_requisition_url in main.py — the disposition
  // param defaults to "attachment" there, so download() above is
  // completely unchanged by this addition).
  const fetchPreview = async () => {
    setLoadingUrl(true); setError(""); setExpired(false);
    if (expiryTimerRef.current) { clearTimeout(expiryTimerRef.current); expiryTimerRef.current = null; }
    try {
      const res = await axios.get(`${API_BASE}/clinician/orders/${orderId}/requisition-url`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { ...(schemaKey ? { schema_key: schemaKey } : {}), disposition: "inline" },
      });
      if (res.data.url) {
        setPreviewUrl(res.data.url);
        setPreviewFetchedAt(Date.now());
        expiryTimerRef.current = setTimeout(() => setExpired(true), REAL_URL_LIFETIME_MS);
      } else {
        setError("Preview not available.");
      }
    } catch (err) {
      setError(extractErrorDetail(err, "Could not load preview right now."));
    } finally {
      setLoadingUrl(false);
    }
  };

  const togglePreview = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    const isStale = !previewUrl || expired || !previewFetchedAt || (Date.now() - previewFetchedAt) > SAFE_URL_LIFETIME_MS;
    if (isStale && !loadingUrl) await fetchPreview();
  };

  return (
    <Box sx={badge ? {
      px: 1, py: 0.5, borderRadius: "8px", border: `1px solid ${badge.accent}33`, bgcolor: `${badge.accent}0d`,
    } : undefined}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: badge ? "space-between" : "flex-start", gap: 1, flexWrap: "wrap" }}>
        {badge && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
            <Box sx={{
              width: 22, height: 22, borderRadius: "7px", bgcolor: badge.accent, flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <badge.Icon sx={{ fontSize: 13, color: "#fff" }} />
            </Box>
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: badge.accent }}>{badge.text}</Typography>
          </Box>
        )}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
          {/* 2026-10-21 fix (issue checklist #61) — download/preview below
              now silently switch to the patient-signed PDF once the
              patient has signed (see get_clinician_order_requisition_url),
              so this chip tells the clinician which version they're
              about to get instead of leaving that invisible. */}
          {patientSigned && (
            <Chip size="small" label="Patient-signed version" sx={{
              fontSize: 11, fontWeight: 600, bgcolor: "rgba(34,197,94,0.1)", color: "#16a34a",
            }} />
          )}
          <Button
            onClick={download} disabled={downloading}
            startIcon={downloading ? <CircularProgress size={14} sx={{ color: BRAND.navy }} /> : <Download sx={{ fontSize: 16 }} />}
            size="small" sx={{ textTransform: "none", color: BRAND.navy, fontWeight: 700 }}
          >
            {downloadLabel}
          </Button>
          <Button size="small" onClick={togglePreview} disabled={loadingUrl}
            startIcon={loadingUrl ? <CircularProgress size={14} sx={{ color: BRAND.navy }} /> : <Visibility sx={{ fontSize: 16 }} />}
            sx={{ textTransform: "none", color: BRAND.navy, fontWeight: 700 }}>
            {open ? "Hide Preview" : "Preview"}
          </Button>
        </Box>
      </Box>
      {open && (
        loadingUrl ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress size={24} sx={{ color: BRAND.navy }} /></Box>
        ) : error ? (
          <Alert severity="warning" sx={{ mt: 1 }}>{error}</Alert>
        ) : previewUrl ? (
          <>
            {expired && (
              <Alert severity="warning" sx={{ mt: 1, mb: 1 }}
                action={<Button size="small" onClick={fetchPreview} sx={{ textTransform: "none" }}>Reload</Button>}>
                Preview expired — click to reload.
              </Alert>
            )}
            <InlinePreviewFrame url={previewUrl} onError={() => setExpired(true)} />
          </>
        ) : null
      )}
    </Box>
  );
}

// Supporting Documents — read-only list (staff/admin can also add these
// from the Lab Orders dashboard; see labariumaireport/frontend/src/
// components/LabOrdersView.js's own SupportingDocsPanel). "View" fetches a
// short-lived signed URL via the clinician-side counterpart of that same
// endpoint, same on-demand-signing pattern as RequisitionRow's download()
// just above.
function SupportingDocViewButton({ orderId, docIndex, token }) {
  const [loading, setLoading] = useState(false);
  // No toast system in this app yet, so a failed fetch briefly swaps the
  // button's own label to a short error message (then reverts to "View")
  // instead of silently resetting with zero feedback.
  const [failed, setFailed] = useState(false);
  const view = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/clinician/orders/${orderId}/supporting-docs/${docIndex}/url`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      window.open(res.data.url, "_blank", "noopener,noreferrer");
    } catch {
      setFailed(true);
      setTimeout(() => setFailed(false), 3000);
    } finally {
      setLoading(false);
    }
  };
  return (
    <Button size="small" onClick={view} disabled={loading}
      sx={{ textTransform: "none", fontSize: 12, color: failed ? "#b91c1c" : BRAND.navy, fontWeight: 700 }}>
      {loading ? <CircularProgress size={13} /> : failed ? "Couldn't open — try again" : "View"}
    </Button>
  );
}

function SupportingDocsList({ order, token, isDark }) {
  const docs = order.supporting_documents || [];
  return (
    <Box sx={{ mt: 2 }}>
      <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.04em", mb: 0.75 }}>
        Supporting Documents
      </Typography>
      {docs.length === 0 ? (
        <Typography sx={{ fontSize: 12.5, color: "#94a3b8" }}>None uploaded.</Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          {docs.map((d, idx) => (
            <Box key={idx} sx={{
              display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1,
              px: 1.25, py: 0.5, borderRadius: "8px",
              border: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,53,122,0.08)",
            }}>
              <Typography sx={{ fontSize: 12.5, color: isDark ? "#e5e7eb" : "#1e293b" }}>
                <strong>{SUPPORTING_DOC_TYPE_LABELS[d.doc_type] || d.doc_type}</strong>
                {d.doc_type === "other" && d.doc_type_other_label ? ` — ${d.doc_type_other_label}` : ""}
                {" — "}{d.filename}
                <span style={{ color: "#94a3b8" }}>
                  {" "}(by {d.uploaded_by_role || "unknown"}{d.uploaded_at ? `, ${formatEST(d.uploaded_at)}` : ""})
                </span>
              </Typography>
              <SupportingDocViewButton orderId={order.order_id} docIndex={idx} token={token} />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

// Part C — per-requisition patient e-signature status + a resend button
// for the clinician-triggered /clinician/orders/{id}/resend-patient-consent
// endpoint. Shows one of: a per-requisition "Signed on [date]" (reading
// requisitions[].status === "patient_signed"), or an order-level
// "Awaiting patient signature" / "No email on file — resend manually"
// derived from patient_consent_status (set once, at submission, and on
// every resend).
function PatientConsentStatus({ order, token }) {
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [resendError, setResendError] = useState("");

  const requisitions = order.requisitions || [];
  const anySigned = requisitions.some((r) => r.status === "patient_signed");
  const allSigned = requisitions.length > 0 && requisitions.every((r) => r.status === "patient_signed");
  // 2026-10-08 — item 1/2 fix: the consent link now goes out over email
  // AND/OR SMS (see _issue_patient_consent_link in main.py), so these two
  // string values now mean something broader than their names suggest:
  // "no_email" is really "no email AND no phone on file" (order
  // submission itself blocks that case now, so this is effectively only
  // reachable if both were later removed from the patient's record before
  // a resend), and "email_failed" is really "every channel that was
  // attempted failed" (could be just email, just SMS, or both). Kept as
  // the same backend string values (never renamed — other tooling/audit
  // dashboards may already read them), just relabeled here.
  // 2026-10-21 fix (chunk-2 line-by-line trace finding, confirmed real) —
  // `resentStatus` tracks the REAL outcome of the last resend attempt
  // (defaulting to the order's own current status until a resend runs),
  // instead of the old `resent` boolean that just meant "the HTTP call
  // returned 200" regardless of whether a link actually went out. The
  // backend now also persists this same status back onto the order doc
  // (see resend_patient_consent_clinician's own fix), so a page refresh
  // shows the real last-known state too, not just this session's memory.
  const [resentStatus, setResentStatus] = useState(order.patient_consent_status);
  const noContactMethod = resentStatus === "no_email";
  const allChannelsFailed = resentStatus === "email_failed";
  // "error" — a genuine crash issuing/re-issuing the link (now audited,
  // see patient_consent_issue_failed/patient_consent_resend_failed) —
  // used to fall through to the default "Awaiting patient signature"
  // label, indistinguishable from a healthy pending order.
  const issueCrashed = resentStatus === "error";

  const resend = async () => {
    setResending(true);
    setResendError("");
    try {
      const res = await axios.post(`${API_BASE}/clinician/orders/${order.order_id}/resend-patient-consent`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const newStatus = res.data?.status;
      setResentStatus(newStatus);
      setResent(newStatus === "sent");
      if (newStatus && newStatus !== "sent") {
        setResendError(
          newStatus === "no_email" ? "This patient has no email or phone on file — add contact info first."
            : newStatus === "email_failed" ? "The link could not be delivered on any channel. Please try again shortly."
            : newStatus === "error" ? "Something went wrong resending the link. Please try again."
            : ""
        );
      }
    } catch (err) {
      setResendError(extractErrorDetail(err, "Could not resend the consent link."));
    } finally {
      setResending(false);
    }
  };

  if (resentStatus === "not_eligible") return null;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1.5 }}>
      <Chip size="small"
        label={
          allSigned ? "Patient signed"
            : anySigned ? "Partially patient-signed"
            : noContactMethod ? "No email or phone on file — resend manually"
            : issueCrashed ? "Could not send consent link — try resending"
            : "Awaiting patient signature"
        }
        sx={{
          fontSize: 11.5, fontWeight: 600,
          bgcolor: allSigned ? "rgba(34,197,94,0.1)" : (noContactMethod || issueCrashed) ? "rgba(239,68,68,0.1)" : "rgba(201,169,110,0.15)",
          color: allSigned ? "#16a34a" : (noContactMethod || issueCrashed) ? "#dc2626" : "#8a6d1f",
        }} />
      {allChannelsFailed && (
        <Chip size="small" label="Consent link failed to send" sx={{ fontSize: 11.5, fontWeight: 600, bgcolor: "rgba(239,68,68,0.1)", color: "#dc2626" }} />
      )}
      {!allSigned && (
        <Button size="small" onClick={resend} disabled={resending || resent}
          sx={{ textTransform: "none", fontSize: 12, fontWeight: 700, color: BRAND.navy }}>
          {resending ? <CircularProgress size={13} /> : resent ? "Link resent" : "Resend consent link"}
        </Button>
      )}
      {resendError && <Typography sx={{ fontSize: 11.5, color: "#dc2626" }}>{resendError}</Typography>}
    </Box>
  );
}

const ORDER_FAMILY_LABELS = {
  proove: "Proove",
  toxicology: "Toxicology",
  infectious_disease_molecular: "Infectious Disease / Molecular PCR",
};

// Mirrors OrderTest.js's FAMILY_META exactly (visual pass, 2026-10-08) —
// same accent/icon per family, so a clinician sees one consistent family
// identity across ordering and order-history. Duplicated rather than
// imported since the two components don't currently share a styling
// module and this is a pure styling pass, not a refactor.
const ORDER_FAMILY_META = {
  proove: { accent: BRAND.navy, icon: Science },
  toxicology: { accent: "#8a6d1f", icon: Medication },
  infectious_disease_molecular: { accent: "#0f766e", icon: Coronavirus },
};
const ORDER_FAMILY_META_DEFAULT = { accent: "#64748b", icon: Science };
const orderFamilyMeta = (family) => ORDER_FAMILY_META[family] || ORDER_FAMILY_META_DEFAULT;

function OrderRow({ order, token, isDark }) {
  const [open, setOpen] = useState(false);
  // Legacy orders (placed before Part B) only ever had a singular
  // `test_id`; new ones carry `test_ids`. Both are normalized to an
  // array by the backend's own list endpoint, but fall back here too in
  // case an older cached response is ever replayed.
  const testIds = order.test_ids && order.test_ids.length ? order.test_ids : (order.test_id ? [order.test_id] : []);
  const requisitions = order.requisitions && order.requisitions.length
    ? order.requisitions
    : [{ schema_key: null, family: null, test_ids: testIds }];

  return (
    <Box sx={{
      border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,53,122,0.1)",
      borderRadius: 3, mb: 1.5, overflow: "hidden",
      bgcolor: isDark ? "#111827" : "#fff",
      boxShadow: isDark ? "0 2px 12px rgba(0,0,0,0.3)" : "0 2px 12px rgba(0,53,122,0.05)", transition: "box-shadow 0.2s",
      "&:hover": { boxShadow: isDark ? "0 6px 20px rgba(0,0,0,0.4)" : "0 6px 20px rgba(0,53,122,0.09)" },
    }}>
      <Box onClick={() => setOpen((o) => !o)} sx={{
        display: "flex", alignItems: "center", gap: 1.5, px: 2.25, py: 1.5, cursor: "pointer",
        bgcolor: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,53,122,0.025)",
        "&:hover": { bgcolor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,53,122,0.05)" },
      }}>
        <Typography sx={{ fontWeight: 700, fontSize: 14.5, color: isDark ? BRAND.gold : BRAND.navy, flex: 1 }}>{testIds.map((tid) => TEST_ID_DISPLAY_NAMES[tid] || tid.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())).join(", ")}</Typography>
        <Chip label={ORDER_STATUS_DISPLAY[order.status] || order.status} size="small" color={order.status === "finalized" ? "success" : "warning"} sx={{ fontWeight: 600 }} />
        <Typography sx={{ fontSize: 13, color: isDark ? "#94a3b8" : "#64748b" }}>{order.submitted_at ? formatEST(order.submitted_at) : ""}</Typography>
        {open ? <ExpandLess sx={{ fontSize: 18, color: "#94a3b8" }} /> : <ExpandMore sx={{ fontSize: 18, color: "#94a3b8" }} />}
      </Box>
      {open && (
        <Box sx={{ px: 2.25, py: 1.75, borderTop: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,53,122,0.06)" }}>
          <Typography sx={{ fontSize: 13, color: isDark ? "#94a3b8" : "#64748b", mb: 1 }}>
            Order ID {order.order_id}. This requisition{requisitions.length > 1 ? "s are" : " is"} final and cannot be edited or deleted.
          </Typography>
          {/* 2026-10-08 fix — consent was captured and audited at order
              time but never shown back anywhere; a clinician had no way
              to glance at a past order and confirm what was recorded.
              2026-10-08 move — in-office patient signing removed:
              patient_consent/biobank_consent are now the PATIENT's own
              decision, given on their own async signing page, not the
              clinician's at submit time. The backend now sends `null`
              for both until the patient actually signs — rendered here
              as an explicit "pending" state, never collapsed into the
              same look as "not recorded"/"not opted in" (a patient who
              hasn't signed yet is not the same fact as one who signed
              and declined). */}
          <Box sx={{ display: "flex", gap: 0.75, mb: 1.5, flexWrap: "wrap" }}>
            <Chip size="small" label={
                order.patient_consent === true ? "Patient consent recorded"
                : order.patient_consent === false ? "Patient consent not recorded"
                : "Patient consent — pending patient signature"
              }
              sx={{
                bgcolor: order.patient_consent === true ? "rgba(34,197,94,0.1)"
                  : order.patient_consent === false ? "rgba(239,68,68,0.1)" : "rgba(148,163,184,0.15)",
                color: order.patient_consent === true ? "#16a34a"
                  : order.patient_consent === false ? "#dc2626" : "#64748b",
                fontSize: 11.5, fontWeight: 600,
              }} />
            {/* 2026-10-13 fix — backend now also stores `biobank_consent:
                null` for an order that HAS been signed but where Biobank
                never applied at all (infectious_disease_molecular_v1-only
                — see main.py's _biobank_applicable_for_order), not just
                for one still awaiting signature. Both used to collapse
                into the same "pending patient decision" label, which — for
                an already-signed order — wrongly read as still awaiting
                the patient. Disambiguated using order.patient_consent
                (already true-only once genuinely signed): null + signed =
                "not applicable to this order", null + not-yet-signed =
                still genuinely pending. */}
            <Chip size="small" label={
                order.biobank_consent === true ? "Biobank: opted in"
                : order.biobank_consent === false ? "Biobank: not opted in"
                : order.patient_consent === true ? "Biobank: not applicable to this order"
                : "Biobank: pending patient decision"
              }
              sx={{
                bgcolor: order.biobank_consent === true ? "rgba(201,169,110,0.15)" : "rgba(148,163,184,0.15)",
                color: order.biobank_consent === true ? "#8a6d1f" : "#64748b",
                fontSize: 11.5, fontWeight: 600,
              }} />
          </Box>
          <PatientConsentStatus order={order} token={token} />
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            {requisitions.map((req, i) => {
              // Single-requisition orders keep the plain button (no
              // family is meaningfully being distinguished from anything
              // else); multi-requisition orders get the same
              // accent/icon row treatment as Step 4's review summary in
              // OrderTest.js, so several download buttons read as a
              // considered list instead of stacked, identical buttons.
              if (requisitions.length <= 1) {
                return (
                  <RequisitionRow
                    key={req.schema_key || i}
                    orderId={order.order_id}
                    schemaKey={req.schema_key}
                    token={token}
                    downloadLabel="Download Requisition PDF"
                    patientSigned={req.status === "patient_signed"}
                  />
                );
              }
              const { accent, icon: Icon } = orderFamilyMeta(req.family);
              return (
                <RequisitionRow
                  key={req.schema_key || i}
                  orderId={order.order_id}
                  schemaKey={req.schema_key}
                  token={token}
                  downloadLabel="Download PDF"
                  badge={{ accent, Icon, text: ORDER_FAMILY_LABELS[req.family] || req.family || "Requisition" }}
                  patientSigned={req.status === "patient_signed"}
                />
              );
            })}
          </Box>
          <SupportingDocsList order={order} token={token} isDark={isDark} />
        </Box>
      )}
    </Box>
  );
}

function PatientDetail({ patientId, token, isDark, onBack }) {
  const [patient, setPatient] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  // 2026-10-10 — the new clinical/billing fields (bill_to/policy_number/
  // sex/height/weight/race/etc.), kept as their OWN piece of state rather
  // than flattened into `form` — DynamicRequisitionForm's onChange hands
  // back the WHOLE data object for its schema each time (same contract
  // OrderTest.js's sharedClinicalData already relies on), so this mirrors
  // that shape exactly instead of merging field-by-field.
  const [clinicalForm, setClinicalForm] = useState(emptyClinicalForm());
  const [schemaSections, setSchemaSections] = useState(null);
  const [saving, setSaving] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  // Labarium identity plan — second, independent address-confirmation
  // safety net (see addressShared.js's useAddressConfirm docstring).
  const { confirmAddress, dialog: addressConfirmDialog } = useAddressConfirm(API_BASE);

  const load = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API_BASE}/clinician/patients/${patientId}`, { headers }),
      axios.get(`${API_BASE}/clinician/orders`, { headers, params: { clinician_patient_id: patientId } }),
    ]).then(([pRes, oRes]) => {
      setPatient(pRes.data);
      setForm({
        first_name: pRes.data.first_name, last_name: pRes.data.last_name, dob: pRes.data.dob,
        address: { line1: pRes.data.address_line1, unit: pRes.data.address_unit, city: pRes.data.address_city, state: pRes.data.address_state, zip: pRes.data.address_zip },
        email: pRes.data.email, phone: pRes.data.phone,
      });
      setClinicalForm(clinicalFormFromPatient(pRes.data));
      setOrders(oRes.data.orders || []);
    }).catch(() => setError("Could not load this patient.")).finally(() => setLoading(false));
  };
  useEffect(load, [patientId, headers]);

  // Fetched once (cached for the life of this component) through this
  // app's own clinician-authenticated schema proxy — same call OrderTest.js
  // makes for Step 3/4's requisition form, reused here purely for its
  // labels/options/types (never re-derives WHICH fields to send; that's
  // CLINICAL_FIELD_IDS above). If this fetch fails (e.g.
  // REPORT_INTERNAL_API_URL unset), identity editing above is completely
  // unaffected — only this section silently stays unrendered, never
  // blocking the rest of the Edit form.
  useEffect(() => {
    axios.get(`${API_BASE}/clinician/order-requisition-schema/${_SCHEMA_KEY_FOR_FIELD_LABELS}`, { headers })
      .then((res) => setSchemaSections(res.data.schema))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Same filtered-copy pattern as OrderTest.js's patientInfoClinicalSection/
  // paymentInfoSection — patient_info's identity fields (first/last/dob/
  // address/phone/email) stay on the custom form above, never duplicated
  // here; only the non-identity clinical subset is pulled from this
  // section, plus payment_info in full.
  const patientInfoClinicalSection = useMemo(() => {
    const section = (schemaSections || []).find((s) => s.id === "patient_info");
    if (!section) return null;
    const keep = new Set(["sex", "height_ft", "height_in", "weight_lbs", "race", "middle_initial"]);
    return { ...section, title: "Additional Patient Information", fields: section.fields.filter((f) => keep.has(f.id)) };
  }, [schemaSections]);
  const paymentInfoSection = useMemo(() => {
    return (schemaSections || []).find((s) => s.id === "payment_info") || null;
  }, [schemaSections]);

  // 2026-10-15 fix (proper mandatory validation, confirmed real gap) —
  // this edit form had NO required-field gating at all, unlike the
  // identical fields on OrderTest.js's own Step 2 (canProceedFromStep).
  // Save was never disabled regardless of blank name/address.
  //
  // 2026-10-19 fix (real crash, confirmed) — this ran unconditionally on
  // EVERY render of PatientDetail, but `form` starts as `null` (useState(null)
  // above) and is only ever set once load()'s async Promise.all resolves.
  // The very first render — before data loads, or if the load() fetch
  // fails before ever calling setForm — hit `form.first_name` on a null
  // form and crashed the whole component with no error boundary, blanking
  // the entire page. Guarded on `form` existing first.
  // 2026-10-22 (Piece 6, one-identity-one-email invariant, downstream-
  // mismatch-checker finding) — email/phone are now mandatory on the
  // shared ClinicianPatientSubmit model (the backend 422s without them),
  // but this gate never required them — a clinician editing ANY
  // unrelated field (e.g. address) on an older patient record with blank
  // contact info on file would hit an opaque 422 with no indication why.
  const canSave = !!(
    form && form.first_name.trim() && form.last_name.trim() && form.dob.trim()
    && form.address.line1.trim() && form.address.city.trim() && form.address.state.trim() && form.address.zip.trim()
    && form.email.trim() && form.phone.trim()
  );

  const save = async () => {
    setError("");
    // Labarium identity plan — forced confirm checkpoint, right before this
    // save, using the backend's authoritative Smarty validation.
    const confirmedAddress = await confirmAddress(form.address);
    if (!confirmedAddress) return; // "No, let me edit" — stay in edit mode
    setSaving(true);
    try {
      const res = await axios.put(`${API_BASE}/clinician/patients/${patientId}`, {
        first_name: form.first_name, last_name: form.last_name, dob: form.dob,
        address_line1: confirmedAddress.line1, address_unit: confirmedAddress.unit || undefined,
        address_city: confirmedAddress.city, address_state: confirmedAddress.state, address_zip: confirmedAddress.zip,
        // 2026-10-22 (Piece 6) — always real, non-blank values now that
        // canSave requires both; the `|| undefined` fallback this used
        // to need (when both were optional) is no longer reachable.
        email: form.email.trim(), phone: form.phone.trim(),
        ...clinicalForm,
      }, { headers });
      setPatient(res.data);
      setClinicalForm(clinicalFormFromPatient(res.data));
      setEditing(false);
    } catch (err) {
      setError(extractErrorDetail(err, "Could not save changes."));
    } finally {
      setSaving(false);
    }
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px", fontSize: 14, bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#fff",
      "& fieldset": { borderColor: isDark ? "rgba(255,255,255,0.12)" : "rgba(0,53,122,0.14)" },
      "&:hover fieldset": { borderColor: isDark ? "rgba(255,255,255,0.3)" : "rgba(0,53,122,0.3)" },
      "&.Mui-focused fieldset": { borderColor: BRAND.navy, borderWidth: "1.5px" },
    },
  };

  if (loading) return <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress sx={{ color: BRAND.navy }} /></Box>;
  if (!patient) return <Typography sx={{ color: "#ef4444", textAlign: "center", py: 6 }}>{error || "Patient not found."}</Typography>;

  return (
    <Box>
      {addressConfirmDialog}
      <Button onClick={onBack} startIcon={<ArrowBack sx={{ fontSize: 16 }} />} sx={{ mb: 2, textTransform: "none", color: "#64748b", fontWeight: 600 }}>
        All Patients
      </Button>
      {error && <Typography sx={{ color: "#ef4444", mb: 2, fontSize: 14 }}>{error}</Typography>}

      <Card sx={{
        borderRadius: 4, mb: 3, overflow: "hidden",
        border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,53,122,0.1)",
        bgcolor: isDark ? "#111827" : "#fff",
        boxShadow: isDark ? "0 4px 24px rgba(0,0,0,0.35)" : "0 4px 24px rgba(0,53,122,0.08)",
      }}>
        <Box sx={{
          px: 3, py: 2.5, display: "flex", justifyContent: "space-between", alignItems: "center",
          background: `linear-gradient(135deg, ${BRAND.navyDark} 0%, ${BRAND.navy} 100%)`,
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.75 }}>
            <Avatar sx={{ width: 44, height: 44, fontSize: 15, fontWeight: 800, bgcolor: "rgba(192,168,140,0.2)", color: BRAND.gold, border: "1.5px solid rgba(192,168,140,0.4)" }}>
              {initialsOf(patient)}
            </Avatar>
            <Typography sx={{ fontWeight: 800, fontSize: 19, color: "#fff" }}>
              {patient.first_name} {patient.last_name}
            </Typography>
          </Box>
          {!editing ? (
            <Button size="small" onClick={() => setEditing(true)}
              sx={{ textTransform: "none", color: BRAND.gold, fontWeight: 700, "&:hover": { bgcolor: "rgba(255,255,255,0.08)" } }}>Edit</Button>
          ) : (
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button size="small" onClick={() => { setEditing(false); load(); }}
                sx={{ textTransform: "none", color: "rgba(255,255,255,0.7)" }}>Cancel</Button>
              <Button size="small" variant="contained" disabled={saving || !canSave} onClick={save}
                sx={{ textTransform: "none", fontWeight: 700, bgcolor: BRAND.gold, color: BRAND.navyDark, "&:hover": { bgcolor: "#dfc08a" } }}>
                {saving ? <CircularProgress size={16} sx={{ color: BRAND.navyDark }} /> : "Save"}
              </Button>
            </Box>
          )}
        </Box>

        <Box sx={{ p: 3 }}>
          {!editing ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography sx={{ fontSize: 14, color: isDark ? "#cbd5e1" : "#475569" }}><strong style={{ color: isDark ? "#f1f5f9" : "#1e293b" }}>DOB</strong> &nbsp;{patient.dob}</Typography>
              <Typography sx={{ fontSize: 14, color: isDark ? "#cbd5e1" : "#475569" }}>
                {patient.address_line1}{patient.address_unit ? `, ${patient.address_unit}` : ""}, {patient.address_city}, {patient.address_state} {patient.address_zip}
              </Typography>
              {patient.email && <Typography sx={{ fontSize: 14, color: isDark ? "#cbd5e1" : "#475569" }}>{patient.email}</Typography>}
              {patient.phone && <Typography sx={{ fontSize: 14, color: isDark ? "#cbd5e1" : "#475569" }}>{patient.phone}</Typography>}
              {/* 2026-10-10 — read-only summary of the clinical/billing
                  fields, when this patient has any set. Kept minimal (no
                  schema fetch needed just to display plain values) —
                  editing is the only place labels/options come from the
                  schema. */}
              {CLINICAL_FIELD_IDS.some((fid) => {
                const v = patient[fid];
                return Array.isArray(v) ? v.length > 0 : !!v;
              }) && (
                <Box sx={{ mt: 1, pt: 1, borderTop: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,53,122,0.08)" }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.5 }}>
                    Clinical &amp; Billing on File
                  </Typography>
                  <Typography sx={{ fontSize: 13.5, color: isDark ? "#cbd5e1" : "#475569" }}>
                    {[
                      patient.sex && `Sex: ${patient.sex}`,
                      patient.bill_to && `Bill to: ${patient.bill_to}`,
                      patient.insurance_provider && `Insurance: ${patient.insurance_provider}`,
                    ].filter(Boolean).join(" · ") || "Some fields on file — see Edit for details."}
                  </Typography>
                </Box>
              )}
            </Box>
          ) : (
            <Box>
              <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1.5, mb: 1.5 }}>
                <TextField hiddenLabel fullWidth label="First name" error={!form.first_name.trim()} sx={inputSx} value={form.first_name} onChange={(e) => setForm((p) => ({ ...p, first_name: e.target.value }))} />
                <TextField hiddenLabel fullWidth label="Last name" error={!form.last_name.trim()} sx={inputSx} value={form.last_name} onChange={(e) => setForm((p) => ({ ...p, last_name: e.target.value }))} />
              </Box>
              <TextField hiddenLabel type="date" error={!form.dob.trim()} sx={{ mb: 1.5, maxWidth: 200, ...inputSx }} value={form.dob} onChange={(e) => setForm((p) => ({ ...p, dob: e.target.value }))} slotProps={{ inputLabel: { shrink: true } }} />
              <AddressFields isDark={isDark} inputSx={inputSx} value={form.address} onChange={(addr) => setForm((p) => ({ ...p, address: addr }))}
                showRequired
                helperText="Update this patient's address if it has changed. This helps us match them to their lab results." />
              {/* 2026-10-22 (Piece 6) — now mandatory, same as the
                  identity fields above (the backend 422s without them) —
                  an older record created before this piece shipped may
                  still have these blank, so the red error state here is
                  the clinician's only cue that Save is disabled because
                  of these two fields specifically, not something else. */}
              <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1.5, mt: 1.5 }}>
                <TextField hiddenLabel fullWidth type="email" placeholder="Email" error={!form.email.trim()} sx={inputSx} value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} />
                <TextField hiddenLabel fullWidth type="tel" placeholder="Phone" error={!form.phone.trim()} sx={inputSx} value={form.phone} onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} />
              </Box>

              {/* 2026-10-10 — clinical/billing fields, now real and
                  editable here, independent of any specific order. Reuses
                  the EXACT labels/types/options order_requisition_schema.py
                  already defines for these same fields (fetched via the
                  schema proxy above), via the same DynamicRequisitionForm
                  OrderTest.js's Step 3/4 uses — never a second, hand-typed
                  copy of these labels. Silently omitted if the schema
                  fetch failed/hasn't landed yet, rather than blocking the
                  rest of this form. */}
              {patientInfoClinicalSection && (
                <Box sx={{ mt: 2.5 }}>
                  <DynamicRequisitionForm
                    schema={[patientInfoClinicalSection]}
                    data={clinicalForm}
                    onChange={setClinicalForm}
                    isDark={isDark}
                  />
                </Box>
              )}
              {paymentInfoSection && (
                <Box sx={{ mt: 1.5 }}>
                  <DynamicRequisitionForm
                    schema={[paymentInfoSection]}
                    data={clinicalForm}
                    onChange={setClinicalForm}
                    isDark={isDark}
                  />
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Card>

      <Typography sx={{ fontWeight: 800, fontSize: 16, color: isDark ? "#f1f5f9" : BRAND.navy, mb: 1.75 }}>Order History</Typography>
      {orders.length === 0 ? (
        <Box sx={{
          textAlign: "center", py: 5, color: "#94a3b8", bgcolor: isDark ? "#111827" : "#fff", borderRadius: 3,
          border: isDark ? "1px dashed rgba(255,255,255,0.15)" : "1px dashed rgba(0,53,122,0.15)",
        }}>
          <Typography sx={{ fontSize: 14 }}>No orders yet for this patient.</Typography>
        </Box>
      ) : (
        orders.map((o) => <OrderRow key={o.order_id} order={o} token={token} isDark={isDark} />)
      )}
    </Box>
  );
}

export default function MyPatients() {
  // 2026-10-13 fix — this page hardcoded light-mode colors throughout
  // despite rendering a working ThemeToggle (via ClinicianPageHeader) —
  // useTheme()/isDark were entirely absent here. Same useTheme() pattern
  // Login.js/Register.js already use correctly. The PatientDetail/
  // PatientCard/OrderRow sub-components below still render with their
  // own hardcoded light colors (a deeper pass than this fix's scope), but
  // the page shell, search field, and empty/loading/error states below —
  // what's visible without drilling into a specific patient — now follow
  // the theme, and the two hardcoded `isDark={false}` props this file
  // passed to AddressFields/DynamicRequisitionForm are fixed to follow
  // the real value too.
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const { patientId } = useParams();
  const { user } = useContext(UserContext);
  const token = user?.token;
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [truncated, setTruncated] = useState(false);

  const load = () => {
    setLoading(true);
    axios.get(`${API_BASE}/clinician/patients`, { headers: { Authorization: `Bearer ${token}` }, params: search ? { search } : {} })
      .then((res) => {
        setPatients(res.data.patients || []);
        setTruncated(!!res.data.truncated);
      })
      .catch(() => setError("Could not load your patients."))
      .finally(() => setLoading(false));
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [token, search]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: isDark ? "#080d1a" : "#f8f6f2" }}>
      <ClinicianPageHeader
        title="My Patients"
        subtitle="Your patients and their order history, in one place."
      />

      <Box sx={{ maxWidth: 760, mx: "auto", px: 3, py: 5 }}>
        {patientId ? (
          <PatientDetail patientId={patientId} token={token} isDark={isDark} onBack={() => navigate("/my-patients")} />
        ) : (
          <>
            <TextField
              fullWidth placeholder="Search by name or date of birth…" value={search} onChange={(e) => setSearch(e.target.value)}
              sx={{
                mb: 3.5,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "14px", bgcolor: isDark ? "#111827" : "#fff", fontSize: 15, height: 54,
                  boxShadow: isDark ? "0 2px 12px rgba(0,0,0,0.3)" : "0 2px 12px rgba(0,53,122,0.05)",
                  "& fieldset": { borderColor: isDark ? "rgba(255,255,255,0.12)" : "rgba(0,53,122,0.12)" },
                  "&:hover fieldset": { borderColor: isDark ? "rgba(255,255,255,0.3)" : "rgba(0,53,122,0.3)" },
                  "&.Mui-focused fieldset": { borderColor: BRAND.navy, borderWidth: "1.5px" },
                },
              }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 20, color: isDark ? BRAND.gold : BRAND.navy, opacity: 0.6, ml: 0.5 }} /></InputAdornment> }}
            />
            {!loading && !error && truncated && (
              <Alert severity="info" sx={{ mb: 3 }}>
                Showing the first 500 patients — refine your search to see more.
              </Alert>
            )}
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress sx={{ color: BRAND.navy }} /></Box>
            ) : error ? (
              <Typography sx={{ color: "#ef4444", textAlign: "center", py: 6 }}>{error}</Typography>
            ) : patients.length === 0 ? (
              <Box sx={{
                textAlign: "center", py: 8, color: "#94a3b8", bgcolor: isDark ? "#111827" : "#fff", borderRadius: 3,
                border: isDark ? "1px dashed rgba(255,255,255,0.15)" : "1px dashed rgba(0,53,122,0.15)",
              }}>
                <Typography sx={{ fontSize: 14.5 }}>No patients yet — add one from Order a Test.</Typography>
              </Box>
            ) : (
              patients.map((p) => <PatientCard key={p.id} patient={p} isDark={isDark} onOpen={(id) => navigate(`/my-patients/${id}`)} />)
            )}
          </>
        )}
      </Box>
    </Box>
  );
}
